import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CanDirective, CapabilityNames, HeaderService, Q8Breakpoints } from 'core';
import { LoadingSpinner, TableColumnInfo, TableView, TypeOfColumn } from 'design-system';
import { finalize } from 'rxjs';
import { Worksheet, WorksheetSearch, WorksheetStatus } from '../../interfaces/worksheet.model';
import { WorksheetsService } from '../../services/worksheets.service';
import { formatDateLabel, toDate } from '../../utils/receipt.utils';
import {
  ReceiptsWorksheetFilterDialogComponent,
  WorksheetFilterDialogData,
  WorksheetFilterDialogResult,
} from '../../components/dialog-components/receipts-worksheet-filter-dialog-component/receipts-worksheet-filter-dialog-component';

interface WorksheetRow {
  id: string;
  numeroBrogliaccio: number;
  data: Date | null;
  importoContanti: number;
  importoAssegni: number;
  importoTotale: number;
  numeroIncassi: number;
  situazione: string;
}

const STATUS_LABELS: Record<string, string> = {
  [WorksheetStatus.Nuovo]: 'Nuovo',
  [WorksheetStatus.Confermato]: 'Confermato',
  [WorksheetStatus.PresoInCarico]: 'Preso in carico',
};

@Component({
  selector: 'rev-receipts-brogliacci',
  imports: [
    CanDirective,
    CurrencyPipe,
    DatePipe,
    FormsModule,
    LoadingSpinner,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatSelectModule,
    NgClass,
    RouterLink,
    TableView,
  ],
  templateUrl: './receipts-brogliacci.html',
  styleUrl: './receipts-brogliacci.scss',
})
export class ReceiptsBrogliacci implements OnInit, OnDestroy {
  private readonly service = inject(WorksheetsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly headerService = inject(HeaderService);
  private filtersDialogRef?: MatDialogRef<ReceiptsWorksheetFilterDialogComponent, WorksheetFilterDialogResult>;

  readonly CAPS = CapabilityNames;

  brogliacci: WorksheetRow[] = [];
  brogliacciTable: (Omit<WorksheetRow, 'data'> & { data: string })[] = [];
  isLoading = false;
  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;

  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  selectedStatuses: WorksheetStatus[] = [];

  readonly statusOptions = [
    { value: WorksheetStatus.Nuovo, label: STATUS_LABELS[WorksheetStatus.Nuovo] },
    { value: WorksheetStatus.Confermato, label: STATUS_LABELS[WorksheetStatus.Confermato] },
    { value: WorksheetStatus.PresoInCarico, label: STATUS_LABELS[WorksheetStatus.PresoInCarico] },
  ];

  readonly brogliacciColumns: TableColumnInfo[] = [
    {
      columnNameDef: 'numeroBrogliaccio',
      label: 'N. brogliaccio',
      type: TypeOfColumn.TEXT,
    },
    {
      columnNameDef: 'data',
      label: 'Data creazione',
      type: TypeOfColumn.TEXT,
    },
    {
      columnNameDef: 'importoContanti',
      label: 'Importo contanti',
      type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'importoAssegni',
      label: 'Importo assegni',
      type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'situazione',
      label: 'Stato',
      type: TypeOfColumn.TEXT,
      cellClass: row => this.statusBadgeClass(row.situazione),
    },
  ];

  statusBadgeClass(status: string): string {
    switch (status) {
      case STATUS_LABELS[WorksheetStatus.PresoInCarico]:
        return 'draft-status-info';
      case STATUS_LABELS[WorksheetStatus.Nuovo]:
        return 'draft-status-warning';
      case STATUS_LABELS[WorksheetStatus.Confermato]:
        return 'draft-status-success';
      default:
        return '';
    }
  }

  ngOnInit(): void {
    this.headerService.setTitle('Brogliacci');
    this.breakpointObserver
      .observe([`(max-width: ${Q8Breakpoints.desktop - 1}px)`])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matches }) => {
        if (matches) {
          this.headerService.setActions([{
            label: 'Mostra filtri',
            icon: 'fa-filter',
            action: () => this.openFiltersDialog(),
          }]);
          return;
        }

        this.filtersDialogRef?.close();
        this.headerService.removeActions();
      });

    this.loadWorksheets();
  }

  ngOnDestroy(): void {
    this.filtersDialogRef?.close();
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  private openFiltersDialog(): void {
    if (this.filtersDialogRef) {
      return;
    }

    const data: WorksheetFilterDialogData = {
      value: {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        statuses: this.selectedStatuses,
      },
      statusOptions: this.statusOptions,
    };

    this.filtersDialogRef = this.dialog.open<
      ReceiptsWorksheetFilterDialogComponent,
      WorksheetFilterDialogData,
      WorksheetFilterDialogResult
    >(ReceiptsWorksheetFilterDialogComponent, { data });

    this.filtersDialogRef.afterClosed().subscribe(result => {
      this.filtersDialogRef = undefined;

      if (result?.action === 'reset') {
        this.dateFrom = null;
        this.dateTo = null;
        this.selectedStatuses = [];
        this.cerca();
      } else if (result?.action === 'apply') {
        this.dateFrom = result.value.dateFrom;
        this.dateTo = result.value.dateTo;
        this.selectedStatuses = result.value.statuses;
        this.cerca();
      }
    });
  }

  loadWorksheets(): void {
    this.isLoading = true;

    const filter: WorksheetSearch = {
      searchObject: {
        dataDa: this.dateFrom ? this.dateFrom.toISOString() : null,
        dataA: this.dateTo ? this.dateTo.toISOString() : null,
        statiBrogliaccio: this.selectedStatuses,
      },
    };

    this.service
      .getWorksheetList(this.pageIndex + 1, this.pageSize, filter)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => {
          this.brogliacci = (data.items ?? []).map(worksheet => this.toRow(worksheet));
          this.brogliacciTable = this.brogliacci.map(brogliaccio => ({
            ...brogliaccio,
            data: formatDateLabel(brogliaccio.data),
          }));
          this.totalItems = data.totalItems ?? 0;
        },
        error: err => {
          console.error('Errore caricamento brogliacci', err);
          this.brogliacci = [];
          this.brogliacciTable = [];
          this.totalItems = 0;
        },
      });
  }

  cerca(): void {
    this.pageIndex = 0;
    this.loadWorksheets();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadWorksheets();
  }

  apriDettaglio(idBrogliaccio: string): void {
    this.router.navigate([idBrogliaccio], { relativeTo: this.route });
  }

  private toRow(worksheet: Worksheet): WorksheetRow {
    return {
      id: worksheet.id,
      numeroBrogliaccio: worksheet.numeroBrogliaccio,
      data: toDate(worksheet.data),
      importoContanti: worksheet.totaleValuta ?? 0,
      importoAssegni: worksheet.totaleAssegni ?? 0,
      importoTotale: worksheet.totale ?? 0,
      numeroIncassi: worksheet.numeroIncassi,
      situazione: STATUS_LABELS[worksheet.statoBrogliaccio] ?? worksheet.statoBrogliaccio,
    };
  }
}
